import React, { useState, useEffect, useCallback } from "react";
import {
  PlusIcon,
  PencilSquareIcon,
  TrashIcon,
  ChartBarIcon,
  PaperAirplaneIcon,
  ArrowPathIcon,
  XMarkIcon,
  PhotoIcon,
  DevicePhoneMobileIcon,
  ComputerDesktopIcon,
  EyeIcon,
  CursorArrowRaysIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ExclamationTriangleIcon,
  MegaphoneIcon,
  ClockIcon,
} from "@heroicons/react/24/outline";
import {
  listMarketingPosts,
  getMarketingPost,
  deleteMarketingPost,
  rebroadcastMarketingPost,
  getAudienceOptions,
  getMarketingPostAnalytics,
} from "../api/marketingPostsAPI";
import PostEditor, { POST_TYPES, EMPTY_FORM, formFromPost } from "./marketing/PostEditor";
import { ConfirmDialog } from "./ChatList";

const PAGE_SIZE = 10;
const STATUS_FILTERS = [
  { value: "", label: "Все" },
  { value: "true", label: "Активные" },
  { value: "false", label: "Архив" },
];

const fmt = (n) => Number(n || 0).toLocaleString("ru-RU");
const fmtDate = (iso) =>
  iso ? new Date(iso).toLocaleString("ru-RU", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : null;

/** Фактическое состояние поста с учётом окна публикации */
const lifecycle = (p) => {
  const now = Date.now();
  if (!p.is_active) return { label: "Архив", cls: "bg-gray-500/15 text-text-muted border-gray-500/30" };
  if (p.publish_at && new Date(p.publish_at).getTime() > now)
    return { label: `Запланирован · ${fmtDate(p.publish_at)}`, cls: "bg-amber-500/15 text-amber-500 border-amber-500/30" };
  if (p.expires_at && new Date(p.expires_at).getTime() < now) return { label: "Истёк", cls: "bg-rose-500/15 text-rose-500 border-rose-500/30" };
  return { label: "Показывается", cls: "bg-emerald-500/15 text-emerald-500 border-emerald-500/30" };
};

const MarketingPostsManagement = () => {
  const [posts, setPosts] = useState([]);
  const [state, setState] = useState("loading");
  const [page, setPage] = useState(1);
  const [typeFilter, setTypeFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [audienceOptions, setAudienceOptions] = useState({ user_types: [], languages: [] });
  const [editor, setEditor] = useState(null); // { editing, initialForm }
  const [openingId, setOpeningId] = useState(null);
  const [toast, setToast] = useState(null);
  const [confirm, setConfirm] = useState(null); // { kind: 'delete'|'rebroadcast', post }
  const [busy, setBusy] = useState(false);
  const [confirmError, setConfirmError] = useState(null);
  const [analyticsFor, setAnalyticsFor] = useState(null);
  const [analytics, setAnalytics] = useState(null);

  useEffect(() => {
    if (!toast || toast.type === "error") return undefined;
    const t = setTimeout(() => setToast(null), 3500);
    return () => clearTimeout(t);
  }, [toast]);

  const fetchPosts = useCallback(async () => {
    setState((s) => (s === "ready" ? "ready" : "loading"));
    try {
      const params = { page, limit: PAGE_SIZE };
      if (typeFilter) params.type = typeFilter;
      if (statusFilter) params.is_active = statusFilter === "true";
      const data = await listMarketingPosts(params);
      setPosts(data.items || []);
      setState("ready");
    } catch (e) {
      console.error("Failed to load posts:", e);
      setState("error");
    }
  }, [page, typeFilter, statusFilter]);

  useEffect(() => {
    fetchPosts();
  }, [fetchPosts]);

  useEffect(() => {
    getAudienceOptions().then(setAudienceOptions).catch(() => {});
  }, []);

  const openCreate = () => setEditor({ editing: null, initialForm: EMPTY_FORM });

  const openEdit = async (post) => {
    setOpeningId(post.id);
    try {
      const full = await getMarketingPost(post.id);
      setEditor({ editing: full, initialForm: formFromPost(full) });
    } catch (e) {
      setToast({ type: "error", text: `Не удалось открыть пост: ${e.message}` });
    } finally {
      setOpeningId(null);
    }
  };

  const runConfirm = async () => {
    setBusy(true);
    setConfirmError(null);
    const { kind, post } = confirm;
    try {
      if (kind === "delete") {
        await deleteMarketingPost(post.id);
        setToast({ type: "success", text: `Пост #${post.id} перенесён в архив` });
        if (posts.length === 1 && page > 1) setPage((p) => p - 1);
      } else {
        const r = await rebroadcastMarketingPost(post.id);
        setToast({ type: "success", text: `Рассылка отправлена: ${fmt(r.sent)} сообщений` });
      }
      setConfirm(null);
      fetchPosts();
    } catch (e) {
      setConfirmError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const showAnalytics = async (post) => {
    setAnalyticsFor(post);
    setAnalytics(null);
    try {
      setAnalytics(await getMarketingPostAnalytics(post.id, 30));
    } catch (e) {
      setAnalytics({ error: e.message });
    }
  };

  const changeFilter = (setter) => (value) => {
    setter(value);
    setPage(1);
  };

  const hasMore = posts.length === PAGE_SIZE;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap gap-2">
          <div className="flex rounded-lg border border-border-primary bg-bg-card p-0.5">
            {[{ value: "", label: "Все типы" }, ...POST_TYPES].map((t) => (
              <button
                key={t.value || "all"}
                type="button"
                onClick={() => changeFilter(setTypeFilter)(t.value)}
                className={`rounded-md px-3 py-1 text-xs font-semibold transition-colors ${
                  typeFilter === t.value ? "bg-bg-tertiary text-text-primary" : "text-text-secondary hover:text-text-primary"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
          <div className="flex rounded-lg border border-border-primary bg-bg-card p-0.5">
            {STATUS_FILTERS.map((s) => (
              <button
                key={s.value || "all"}
                type="button"
                onClick={() => changeFilter(setStatusFilter)(s.value)}
                className={`rounded-md px-3 py-1 text-xs font-semibold transition-colors ${
                  statusFilter === s.value ? "bg-bg-tertiary text-text-primary" : "text-text-secondary hover:text-text-primary"
                }`}
              >
                {s.label}
              </button>
            ))}
          </div>
        </div>
        <div className="flex gap-2">
          <button type="button" onClick={fetchPosts} title="Обновить" aria-label="Обновить" className="rounded-lg border border-border-primary p-2 text-text-secondary hover:bg-bg-tertiary">
            <ArrowPathIcon className={`h-4 w-4 ${state === "loading" ? "animate-spin" : ""}`} />
          </button>
          <button type="button" onClick={openCreate} className="inline-flex items-center gap-2 rounded-lg bg-accent-grad px-4 py-2 text-sm font-semibold text-white hover:opacity-90">
            <PlusIcon className="h-4 w-4" />
            Новый пост
          </button>
        </div>
      </div>

      {toast && (
        <div
          role={toast.type === "error" ? "alert" : "status"}
          className={`flex items-start justify-between gap-3 rounded-lg border px-4 py-3 text-sm ${
            toast.type === "error" ? "border-rose-500/30 bg-rose-500/10 text-rose-500" : "border-emerald-500/30 bg-emerald-500/10 text-emerald-500"
          }`}
        >
          <span>{toast.text}</span>
          <button type="button" onClick={() => setToast(null)} aria-label="Закрыть" className="shrink-0 opacity-70 hover:opacity-100">
            <XMarkIcon className="h-4 w-4" />
          </button>
        </div>
      )}

      <section className="overflow-hidden rounded-block border border-border-primary bg-bg-card shadow-sm">
        {state === "error" ? (
          <div className="flex flex-col items-center gap-3 px-6 py-14 text-center">
            <ExclamationTriangleIcon className="h-10 w-10 text-rose-500/80" />
            <p className="text-sm text-text-secondary">Не удалось загрузить посты</p>
            <button type="button" onClick={fetchPosts} className="rounded-lg border border-border-primary px-3 py-1.5 text-sm font-medium text-text-secondary hover:bg-bg-tertiary">
              Повторить
            </button>
          </div>
        ) : state === "loading" && posts.length === 0 ? (
          <ul className="divide-y divide-border-primary">
            {Array.from({ length: 5 }).map((_, i) => (
              <li key={i} className="flex animate-pulse items-center gap-4 px-4 py-3">
                <div className="h-14 w-14 rounded-lg bg-bg-tertiary" />
                <div className="flex-1 space-y-2">
                  <div className="h-3 w-1/2 rounded bg-bg-tertiary" />
                  <div className="h-3 w-1/3 rounded bg-bg-tertiary" />
                </div>
              </li>
            ))}
          </ul>
        ) : posts.length === 0 ? (
          <div className="px-6 py-16 text-center">
            <MegaphoneIcon className="mx-auto mb-3 h-12 w-12 text-text-muted" />
            <p className="text-sm font-medium text-text-primary">{typeFilter || statusFilter ? "Постов с такими фильтрами нет" : "Постов пока нет"}</p>
            <button type="button" onClick={openCreate} className="mt-3 text-sm font-medium text-[#7C3AED] hover:underline dark:text-indigo-300">
              Создать первый пост
            </button>
          </div>
        ) : (
          <ul className={`divide-y divide-border-primary ${state === "loading" ? "opacity-60" : ""}`}>
            {posts.map((p) => {
              const type = POST_TYPES.find((t) => t.value === p.type) || POST_TYPES[2];
              const life = lifecycle(p);
              const ctr = p.views_count ? ((p.clicks_count / p.views_count) * 100).toFixed(1) : null;
              return (
                <li key={p.id} className="group flex flex-col gap-3 px-4 py-3 hover:bg-bg-tertiary/40 md:flex-row md:items-center">
                  <button type="button" onClick={() => openEdit(p)} className="flex min-w-0 flex-1 items-center gap-3 text-left">
                    {p.image_url ? (
                      <img src={p.image_url} alt="" loading="lazy" className="h-14 w-14 shrink-0 rounded-lg bg-bg-tertiary object-cover" onError={(e) => (e.currentTarget.style.visibility = "hidden")} />
                    ) : (
                      <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-lg bg-bg-tertiary text-text-muted">
                        <PhotoIcon className="h-6 w-6" />
                      </div>
                    )}
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className={`shrink-0 rounded-md border px-1.5 py-px text-[11px] font-semibold ${type.cls}`}>{type.label}</span>
                        <span className="truncate text-sm font-semibold text-text-primary" title={p.title}>
                          {p.title}
                        </span>
                        <span className="shrink-0 font-mono text-[11px] text-text-muted">#{p.id}</span>
                        {openingId === p.id && <ArrowPathIcon className="h-3.5 w-3.5 shrink-0 animate-spin text-text-muted" />}
                      </div>
                      <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-text-secondary">
                        <span className={`rounded-md border px-1.5 py-px font-medium ${life.cls}`}>{life.label}</span>
                        {p.display_modes?.includes("mobile_story") && (
                          <span className="inline-flex items-center gap-1" title="Сториз в приложении">
                            <DevicePhoneMobileIcon className="h-3.5 w-3.5" /> сториз
                          </span>
                        )}
                        {p.display_modes?.includes("web_banner") && (
                          <span className={`inline-flex items-center gap-1 ${p.banner_active ? "text-emerald-500" : ""}`} title={p.banner_active ? "Текущий баннер сайта" : "Баннер (не активен)"}>
                            <ComputerDesktopIcon className="h-3.5 w-3.5" /> баннер{p.banner_active && " · текущий"}
                          </span>
                        )}
                        {p.broadcast_to_chat && (
                          <span className={`inline-flex items-center gap-1 ${p.broadcast_sent_at ? "text-emerald-500" : "text-amber-500"}`}>
                            <PaperAirplaneIcon className="h-3.5 w-3.5" />
                            {p.broadcast_sent_at ? `разослано ${fmt(p.broadcast_recipients_count)}` : "рассылка ожидает"}
                          </span>
                        )}
                        {p.expires_at && p.is_active && (
                          <span className="inline-flex items-center gap-1 text-text-muted">
                            <ClockIcon className="h-3.5 w-3.5" /> до {fmtDate(p.expires_at)}
                          </span>
                        )}
                      </div>
                    </div>
                  </button>

                  <div className="flex items-center justify-between gap-4 md:justify-end">
                    <div className="flex gap-4 text-xs text-text-secondary">
                      <span className="inline-flex items-center gap-1" title="Просмотры">
                        <EyeIcon className="h-4 w-4" /> <span className="tabular-nums text-text-primary">{fmt(p.views_count)}</span>
                      </span>
                      <span className="inline-flex items-center gap-1" title="Клики">
                        <CursorArrowRaysIcon className="h-4 w-4" /> <span className="tabular-nums text-text-primary">{fmt(p.clicks_count)}</span>
                      </span>
                      <span className="w-12 tabular-nums" title="CTR">
                        {ctr === null ? "—" : `${ctr}%`}
                      </span>
                    </div>
                    <div className="flex items-center gap-0.5">
                      <button type="button" onClick={() => openEdit(p)} title="Редактировать" aria-label="Редактировать" className="rounded-lg p-2 text-text-muted hover:bg-bg-tertiary hover:text-text-primary">
                        <PencilSquareIcon className="h-4 w-4" />
                      </button>
                      <button type="button" onClick={() => showAnalytics(p)} title="Метрики за 30 дней" aria-label="Метрики" className="rounded-lg p-2 text-text-muted hover:bg-bg-tertiary hover:text-text-primary">
                        <ChartBarIcon className="h-4 w-4" />
                      </button>
                      {p.broadcast_to_chat && p.is_active && (
                        <button
                          type="button"
                          onClick={() => {
                            setConfirmError(null);
                            setConfirm({ kind: "rebroadcast", post: p });
                          }}
                          title="Переотправить рассылку"
                          aria-label="Переотправить рассылку"
                          className="rounded-lg p-2 text-text-muted hover:bg-bg-tertiary hover:text-text-primary"
                        >
                          <PaperAirplaneIcon className="h-4 w-4" />
                        </button>
                      )}
                      {p.is_active && (
                      <button
                        type="button"
                        onClick={() => {
                          setConfirmError(null);
                          setConfirm({ kind: "delete", post: p });
                        }}
                        title="В архив"
                        aria-label="В архив"
                        className="rounded-lg p-2 text-text-muted hover:bg-rose-500/10 hover:text-rose-500"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                      )}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}

        {state !== "error" && (page > 1 || hasMore) && (
          <div className="flex items-center justify-end gap-2 border-t border-border-primary px-4 py-3">
            <button
              type="button"
              onClick={() => setPage((x) => Math.max(1, x - 1))}
              disabled={page <= 1}
              aria-label="Предыдущая страница"
              className="rounded-lg border border-border-primary p-1.5 text-text-secondary hover:bg-bg-tertiary disabled:cursor-not-allowed disabled:opacity-40"
            >
              <ChevronLeftIcon className="h-4 w-4" />
            </button>
            <span className="min-w-[3rem] text-center text-sm font-medium text-text-primary">{page}</span>
            <button
              type="button"
              onClick={() => setPage((x) => x + 1)}
              disabled={!hasMore}
              aria-label="Следующая страница"
              className="rounded-lg border border-border-primary p-1.5 text-text-secondary hover:bg-bg-tertiary disabled:cursor-not-allowed disabled:opacity-40"
            >
              <ChevronRightIcon className="h-4 w-4" />
            </button>
          </div>
        )}
      </section>

      {editor && (
        <PostEditor
          editing={editor.editing}
          initialForm={editor.initialForm}
          audienceOptions={audienceOptions}
          onClose={() => setEditor(null)}
          onSaved={(_, message) => {
            setEditor(null);
            setToast({ type: "success", text: message });
            fetchPosts();
          }}
        />
      )}

      {confirm && (
        <ConfirmDialog
          title={confirm.kind === "delete" ? `Перенести пост #${confirm.post.id} в архив?` : `Переотправить рассылку #${confirm.post.id}?`}
          text={
            confirm.kind === "delete"
              ? `«${confirm.post.title}» перестанет показываться в сториз и на баннере сайта и останется в разделе «Архив». Отправленные сообщения в чатах не удаляются.`
              : `Сообщение повторно придёт в чаты всей аудитории поста (в прошлый раз — ${fmt(confirm.post.broadcast_recipients_count)} получателей). Отменить нельзя.`
          }
          confirmLabel={confirm.kind === "delete" ? "В архив" : "Переотправить"}
          busy={busy}
          error={confirmError}
          onConfirm={runConfirm}
          onCancel={() => setConfirm(null)}
        />
      )}

      {analyticsFor && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" role="presentation" onMouseDown={(e) => e.target === e.currentTarget && setAnalyticsFor(null)}>
          <div role="dialog" aria-modal="true" className="w-full max-w-lg rounded-block border border-border-primary bg-bg-card p-5 text-left shadow-lg">
            <div className="mb-4 flex items-start justify-between gap-3">
              <div className="min-w-0">
                <h3 className="text-base font-semibold text-text-primary">Метрики · последние 30 дней</h3>
                <p className="truncate text-sm text-text-secondary">
                  #{analyticsFor.id} · {analyticsFor.title}
                </p>
              </div>
              <button type="button" onClick={() => setAnalyticsFor(null)} aria-label="Закрыть" className="rounded-lg p-1 text-text-muted hover:bg-bg-tertiary">
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
            {!analytics ? (
              <div className="grid animate-pulse grid-cols-3 gap-3">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="h-20 rounded-lg bg-bg-tertiary" />
                ))}
              </div>
            ) : analytics.error ? (
              <p className="text-sm text-rose-500">Не удалось загрузить: {analytics.error}</p>
            ) : (
              <>
                <div className="grid grid-cols-3 gap-3">
                  {[
                    ["Просмотры", fmt(analytics.views?.total), `всего ${fmt(analytics.views?.lifetime)}`],
                    ["Клики", fmt(analytics.clicks?.total), null],
                    ["CTR", `${analytics.clicks?.ctr ?? 0}%`, "клики / просмотры"],
                  ].map(([label, value, hint]) => (
                    <div key={label} className="rounded-lg border border-border-primary p-3">
                      <p className="text-xs text-text-secondary">{label}</p>
                      <p className="mt-1 text-2xl font-bold tabular-nums text-text-primary">{value}</p>
                      {hint && <p className="text-[11px] text-text-muted">{hint}</p>}
                    </div>
                  ))}
                </div>
                {analytics.broadcast?.sent_at && (
                  <p className="mt-3 flex items-center gap-2 rounded-lg bg-bg-primary px-3 py-2 text-sm text-text-secondary">
                    <PaperAirplaneIcon className="h-4 w-4 text-[#7C3AED]" />
                    Рассылка {new Date(analytics.broadcast.sent_at).toLocaleString("ru-RU")}: <b className="text-text-primary">{fmt(analytics.broadcast.recipients_count)}</b> получателей
                  </p>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default MarketingPostsManagement;
